import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl, getFullImageUrl, getHeaders } from '../services/api';

const SystemSettingsContext = createContext(null);

export function SystemSettingsProvider({ children }) {
    const [settings, setSettings] = useState({
        systemName: '사출 통합 관제 시스템',
        companyName: '',
        logoUrl: '',
        faviconUrl: '',
        refreshInterval: '5초',
        timezone: 'Asia/Seoul',
        notifyWeb: true,
        // 실시간 샷 현황 표시 옵션
        shotHighlightEnabled: true,
        shotDeltaEnabled: false,
        shotSortByUpdate: false
    });
    const [loading, setLoading] = useState(true);

    // 시스템 설정 조회
    const fetchSettings = useCallback(async () => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${getApiBaseUrl()}/system/settings`, {
                method: 'GET',
                headers: getHeaders()
            });
            const result = await response.json();
            if (result.success && result.data) {
                const data = result.data;
                setSettings({
                    systemName: data.systemName || '사출 통합 관제 시스템',
                    companyName: data.companyName || '',
                    logoUrl: data.logoUrl || '',
                    faviconUrl: data.faviconUrl || '',
                    refreshInterval: data.refreshInterval || '30초',
                    timezone: data.timezone || 'Asia/Seoul',
                    notifyWeb: data.notifyWeb !== undefined ? data.notifyWeb : true,
                    // 실시간 샷 현황 표시 옵션
                    shotHighlightEnabled: data.shotHighlightEnabled !== undefined ? data.shotHighlightEnabled : true,
                    shotDeltaEnabled: data.shotDeltaEnabled !== undefined ? data.shotDeltaEnabled : false,
                    shotSortByUpdate: data.shotSortByUpdate !== undefined ? data.shotSortByUpdate : false
                });

                // 파비콘 적용
                if (data.faviconUrl) {
                    updateFavicon(data.faviconUrl);
                }
            }
        } catch (error) {
            console.error('시스템 설정 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // 파비콘 동적 업데이트
    const updateFavicon = (url) => {
        if (!url) return;

        // 전체 URL로 변환 (상대 경로인 경우 백엔드 URL 추가)
        const fullUrl = getFullImageUrl(url);

        // 기존 favicon 링크 제거
        const existingLinks = document.querySelectorAll("link[rel*='icon']");
        existingLinks.forEach(link => link.remove());

        // 새 favicon 추가
        const link = document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'icon';
        link.href = fullUrl;
        document.head.appendChild(link);

        // apple-touch-icon도 업데이트
        const appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        appleLink.href = fullUrl;
        document.head.appendChild(appleLink);
    };

    // 설정 갱신 함수 (SystemManagementPage에서 호출용)
    const refreshSettings = useCallback(() => {
        fetchSettings();
    }, [fetchSettings]);

    // 초기 로드 및 로그인 상태 변경 감지
    useEffect(() => {
        fetchSettings();

        // storage 이벤트로 로그인/로그아웃 감지
        const handleStorageChange = (e) => {
            if (e.key === 'token' || e.key === 'user') {
                fetchSettings();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [fetchSettings]);

    return (
        <SystemSettingsContext.Provider value={{ settings, loading, refreshSettings, updateFavicon }}>
            {children}
        </SystemSettingsContext.Provider>
    );
}

export function useSystemSettings() {
    const context = useContext(SystemSettingsContext);
    if (!context) {
        throw new Error('useSystemSettings must be used within a SystemSettingsProvider');
    }
    return context;
}

export default SystemSettingsContext;
